import React, { useCallback, useEffect, useState } from 'react';

import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import { updateAccountsMapping, useAccountsMapping } from 'src/services/accountsMapping';
import { openCreateRuleDialog, useAllRules } from 'src/services/rules';
import { openSidebarVisibilityMigrationModal, setSidebarVisibleByDefault } from 'src/services/sidebarVisibility';
import { openWebhookValidationModal } from 'src/services/webhooksValidator';

import { trackEvent } from 'src/lib/analytics';
import jira from 'src/lib/api/jira';

import Toggle from 'src/views/components/Toggle';

import FilterInput from './FilterInput';

const AutoUpdateWebhooksToggle = () => {
  const [accountsMapping] = useAccountsMapping();
  const automatic = !(accountsMapping && accountsMapping.metadata && accountsMapping.metadata.disable_automatic_webhooks_update);

  const [{ loading, error }, setSubmit] = useState({});
  const handleChange = useCallback(() => {
    setSubmit({ loading: true });
    updateAccountsMapping({ metadata: { ...accountsMapping.metadata || {}, disable_automatic_webhooks_update: automatic } })
      .then(() => setSubmit({}))
      .catch(error => setSubmit({ error }));
  }, [accountsMapping, automatic]);

  return (
    <Toggle
      isDisabled={!accountsMapping || loading}
      isError={Boolean(error)}
      isChecked={(automatic && !loading) || (!automatic && loading)}
      onChange={handleChange}
    />
  );
};

const ChangeProjectsSidebarVisibility = ({ openModal }) => {
  const pluginVersion = jira.getPluginVersion();
  const [{ loading, sidebarVisibleByDefaultValue }, setState] = useState({ loading: true });
  const { rules } = useAllRules();

  useEffect(() => {
    async function getSidebarVisibilityByDefault() {
      setState({ sidebarVisibleByDefaultValue, loading: true });
      const sidebarVisibleByDefault = await jira.getSettings(['sidebarVisibleByDefault']);
      const visibleByDefaultValue = sidebarVisibleByDefault?.settings?.sidebarVisibleByDefault?.value;
      setState({ sidebarVisibleByDefaultValue: visibleByDefaultValue, loading: false });
    }
    getSidebarVisibilityByDefault();
  }, [sidebarVisibleByDefaultValue]);

  const handleChange = useCallback(async () => {
    setState({ sidebarVisibleByDefaultValue, isLoading: true });
    const projects = rules.map(r => r?.config?.projects).concat().flat();

    // Do not open the modal if no projects to process
    if (sidebarVisibleByDefaultValue === undefined && pluginVersion && projects.length > 0) {
      openModal({ afterClose: () => setState({ sidebarVisibleByDefaultValue: 'false' }) });
    } else {
      const value = sidebarVisibleByDefaultValue === 'false' ? 'true' : 'false';
      await setSidebarVisibleByDefault(value);
      setState({ sidebarVisibleByDefaultValue: value, isLoading: false });
      trackEvent('sidebar_default_visibility_toggle', { action: sidebarVisibleByDefaultValue === 'false' ? 'enable' : 'disable' });
    }
  }, [openModal, pluginVersion, rules, sidebarVisibleByDefaultValue]);

  return (
    <Toggle
      isDisabled={!pluginVersion || loading}
      isChecked={ sidebarVisibleByDefaultValue !== 'false'}
      onChange={handleChange}
    />
  );
};

const TableHeading = ({ setQuery }) => {
  const pluginVersion = jira.getPluginVersion();
  const [browseBaseUrl, setBrowserUrl] = useState();
  const { rules } = useAllRules();
  /**
   * Used to get `browseBaseUrl` to allow link
   * navigation in the "upgrade Jira plugin" link
   */
  useEffect(() => {
    async function getJiraContext() {
      const { browseBaseUrl } = await jira.getContext();
      setBrowserUrl(browseBaseUrl);
    }
    getJiraContext();
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '20px 0' }}>
      <Heading size="medium">Mapping rules</Heading>
      <div style={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-end' }}>
        {rules.length > 0 && <><div style={{ marginBottom: 20 }}>
          <Button onClick={openWebhookValidationModal}>Check webhooks...</Button>
        </div><div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span>Automatically update PagerDuty and Jira webhooks after Rule save</span>
          <AutoUpdateWebhooksToggle />
        </div></>}
        <div data-cy="visibility-toggle" style={{ display: 'flex', alignItems: 'center' }}>
          <span>Show PagerDuty sidebar on non-connected projects by default</span>

          <ChangeProjectsSidebarVisibility openModal={openSidebarVisibilityMigrationModal} />
          <Tooltip position="top-end" content={
            <span>
              This action applies to all projects. Visibility can also be changed at the project level.
              <a href="https://support.pagerduty.com/docs/jira-server#in-the-rules-settings-page-what-happens-if-i-use-the-toggle-show-pagerduty-sidebar-on-projects-by-default" onClick={trackEvent('sidebar_default_visibility_learnmore')} target="_blank" rel="noopener noreferrer" style={{ color: 'lightskyblue' }}>&nbsp;Learn more.</a>
            </span>
          }>
            <InfoIcon size="small" />
          </Tooltip>
        </div>
        <div style={{ display: 'flex', marginBottom: 20 }}>
          {!pluginVersion && <span style={{ fontSize: '11px' }}>You need to <a target="_parent" onClick={trackEvent('sidebar_upgrade_plugin_link')} href={`${browseBaseUrl}/plugins/servlet/upm?source=side_nav_manage_addons`} >upgrade Jira plugin</a> to change this preference.</span>}
        </div>
        {rules.length > 0 && <div style={{ display: 'flex' }}>
          <FilterInput setQuery={setQuery}/>
          <div style={{ width: '20px' }}/>
          <Button appearance="primary" onClick={openCreateRuleDialog}>Create new Rule</Button>
        </div>}
      </div>
    </div>
  );
};

export default TableHeading;
